import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios, { scheduleSessionExpiryLogout } from "../../lib/http/axiosConfig";
import "./Auth.css";
import logo from "../../Styles/image/logo.png";
import { getDeviceId } from "../../lib/deviceId";

// 배경 위성 타일 (ArcGIS World Imagery, z=17) — 행(y) 4 × 열(x) 5
const TILE_ROWS = [51899, 51900, 51901, 51902];
const TILE_COLS = [111714, 111715, 111716, 111717, 111718];
const FLIGHT_PATH = "M -60 690 C 260 620, 340 470, 620 500 S 1080 640, 1500 420";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reason = new URLSearchParams(location.search).get("reason");
  const returnTo =
    reason === "session-expired"
      ? "/"
      : normalizeReturnTo(location.state?.returnTo) ?? "/";

  // 로그인 상태 체크
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      // 이미 로그인된 상태라면 메인 페이지로 리다이렉트
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (reason === "session-expired") {
      setError("세션이 만료되었습니다. 다시 로그인해주세요.");
    }
  }, [reason]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { data } = await axios.post("/api/auth/login", {
        ...formData,
        deviceId: getDeviceId(),
      });
      const payload = data.data;

      if (payload) {
        // 토큰을 localStorage에 저장
        const accessTokenExpAt = Date.now() + (payload.accessTokenExpiresInSec ?? 0) * 1000;
        localStorage.setItem("accessToken", payload.accessToken);
        localStorage.setItem(
          "accessTokenExpAt",
          String(accessTokenExpAt)
        );
        localStorage.removeItem("refreshToken");
        localStorage.setItem("role", payload.role);
        const role = (payload.role ?? "").toUpperCase();
        if (role === "ADMIN" && payload.refreshTokenExpiresAt) {
          const refreshTokenExpAt = Date.parse(payload.refreshTokenExpiresAt);
          if (!Number.isNaN(refreshTokenExpAt)) {
            localStorage.setItem("refreshTokenExpAt", String(refreshTokenExpAt));
          }
        } else {
          localStorage.removeItem("refreshTokenExpAt");
        }
        scheduleSessionExpiryLogout(payload.role, accessTokenExpAt, payload.refreshTokenExpiresAt);

        // 메인 페이지로 리다이렉트
        navigate(returnTo);
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="auth-container">
      <div className="auth-bg">
        <div className="auth-tiles">
          {TILE_ROWS.map((row) =>
            TILE_COLS.map((col) => (
              <img
                key={`${row}-${col}`}
                src={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/17/${row}/${col}`}
                alt=""
              />
            ))
          )}
        </div>
        <svg
          className="auth-flight"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
        >
          <path className="auth-flight-path" d={FLIGHT_PATH} />
          <circle className="auth-flight-dot" r="3.5">
            <animateMotion dur="30s" repeatCount="indefinite" path={FLIGHT_PATH} />
          </circle>
        </svg>
        <div className="auth-fade" />
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Tlatfarm Logo" />
          <p>스마트 농장 관제 시스템에 로그인하세요</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group with-icon">
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="아이디"
              autoComplete="username"
            />
            <span className="field-icon">
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
              </svg>
            </span>
          </div>

          <div className="form-group with-icon">
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="비밀번호"
              autoComplete="current-password"
            />
            <span className="field-icon">
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <rect x="4" y="10" width="16" height="10" rx="2.5" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? "로그인 중..." : "로그인"}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/privacy" className="link-button">
            개인정보처리방침
          </Link>
        </div>
      </div>

      <div className="auth-copy">
        © 2026 Turbine Crew · TlatFarm Precision Farming OS
      </div>
    </div>
  );
};

export default LoginPage;

function normalizeReturnTo(value) {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  if (v.includes("://")) return null;
  if (v.includes("\\") || v.includes(";") || v.includes(",")) return null;
  return v;
}
